import { loadYoutubeIframeAPI, type YoutubeNamespace } from './loadYoutubeIframeApi';

export type YoutubePlayerState =
  | 'unstarted'
  | 'ended'
  | 'playing'
  | 'paused'
  | 'buffering'
  | 'cued';

export interface YoutubePlaybackMatch {
  id: string | number;
  youtubeVideoId: string;
  startMs?: number | null;
  endMs?: number | null;
}

export interface LoadMatchOptions {
  autoplay?: boolean;
  muted?: boolean;
}

type PlayerStateMap = {
  UNSTARTED: number;
  ENDED: number;
  PLAYING: number;
  PAUSED: number;
  BUFFERING: number;
  CUED: number;
};

type YoutubePlayerEventMap = {
  ready: void;
  stateChange: { state: YoutubePlayerState; rawState: number; matchId: YoutubePlaybackMatch['id'] | null };
  progress: { positionMs: number; durationMs: number | null; matchId: YoutubePlaybackMatch['id'] | null };
  ended: { reason: 'end_ms' | 'youtube_end'; matchId: YoutubePlaybackMatch['id'] | null };
  autoplayBlocked: { matchId: YoutubePlaybackMatch['id'] | null };
  error: { code: number; message: string; matchId: YoutubePlaybackMatch['id'] | null };
};

type EventKey = keyof YoutubePlayerEventMap;
type EventListener<K extends EventKey> = (payload: YoutubePlayerEventMap[K]) => void;

interface YoutubePlayerInstance {
  loadVideoById: (options: { videoId: string; startSeconds?: number; endSeconds?: number }) => void;
  cueVideoById: (options: { videoId: string; startSeconds?: number; endSeconds?: number }) => void;
  playVideo: () => void;
  pauseVideo: () => void;
  stopVideo: () => void;
  destroy: () => void;
  getIframe: () => HTMLIFrameElement;
  getCurrentTime: () => number;
  getDuration: () => number;
  getPlayerState: () => number;
  seekTo: (seconds: number, allowSeekAhead: boolean) => void;
  mute?: () => void;
  unMute?: () => void;
  isMuted?: () => boolean;
}

type PlayerEvent = { data: number; target: YoutubePlayerInstance };

type PlayerErrorEvent = { data: number };

const DEFAULT_PLAYER_STATES: PlayerStateMap = {
  UNSTARTED: -1,
  ENDED: 0,
  PLAYING: 1,
  PAUSED: 2,
  BUFFERING: 3,
  CUED: 5,
};

const AUTOPLAY_BLOCK_TIMEOUT_MS = 5000;
const PROGRESS_UPDATE_INTERVAL_MS = 500;

export class YoutubePlayerManager {
  private static instance: YoutubePlayerManager | null = null;

  static getInstance(): YoutubePlayerManager {
    if (!YoutubePlayerManager.instance) {
      YoutubePlayerManager.instance = new YoutubePlayerManager();
    }
    return YoutubePlayerManager.instance;
  }

  private container: HTMLElement | null = null;
  private namespace: YoutubeNamespace | null = null;
  private player: YoutubePlayerInstance | null = null;
  private playerReady = false;
  private readyPromise: Promise<void> | null = null;
  private resolveReady: (() => void) | null = null;
  private listeners: Map<EventKey, Set<EventListener<any>>> = new Map();
  private progressIntervalId: number | null = null;
  private autoplayTimeoutId: number | null = null;
  private awaitingAutoplay = false;
  private currentMatch: YoutubePlaybackMatch | null = null;
  private playerStates: PlayerStateMap = DEFAULT_PLAYER_STATES;
  private pendingStartSeconds: number | null = null;
  private pendingEndSeconds: number | null = null;

  attach(container: HTMLElement) {
    this.container = container;
    if (this.player) {
      const iframe = this.player.getIframe();
      if (iframe && container.contains(iframe)) {
        return;
      }
      container.innerHTML = '';
      container.appendChild(iframe);
    }
  }

  detach() {
    this.container = null;
  }

  async loadMatch(match: YoutubePlaybackMatch, options: LoadMatchOptions = {}): Promise<void> {
    if (!match.youtubeVideoId) {
      throw new Error('YoutubePlayerManager.loadMatch requires a youtubeVideoId.');
    }

    if (!this.container) {
      throw new Error('YoutubePlayerManager requires an attached container before loading.');
    }

    this.currentMatch = match;
    this.pendingStartSeconds =
      typeof match.startMs === 'number' && !Number.isNaN(match.startMs) && match.startMs > 0
        ? match.startMs / 1000
        : 0;
    this.pendingEndSeconds =
      typeof match.endMs === 'number' && match.endMs !== null && !Number.isNaN(match.endMs)
        ? Math.max(match.endMs / 1000, this.pendingStartSeconds ?? 0)
        : null;

    await this.ensurePlayer();
    if (!this.player) {
      throw new Error('YouTube player failed to initialize.');
    }

    if (options.muted && this.player.mute) {
      this.player.mute();
    } else if (!options.muted && this.player.unMute && this.player.isMuted?.()) {
      this.player.unMute();
    }

    this.awaitingAutoplay = options.autoplay !== false;
    this.clearAutoplayTimeout();
    if (this.awaitingAutoplay) {
      this.autoplayTimeoutId = window.setTimeout(() => {
        if (this.awaitingAutoplay) {
          this.awaitingAutoplay = false;
          this.emit('autoplayBlocked', { matchId: match.id ?? null });
        }
      }, AUTOPLAY_BLOCK_TIMEOUT_MS);
    }

    const videoArgs = {
      videoId: match.youtubeVideoId,
      startSeconds: this.pendingStartSeconds ?? 0,
      endSeconds: this.pendingEndSeconds ?? undefined,
    };

    if (this.awaitingAutoplay) {
      this.player.loadVideoById(videoArgs);
    } else {
      this.player.cueVideoById(videoArgs);
    }

    this.emit('stateChange', {
      state: 'buffering',
      rawState: this.playerStates.BUFFERING,
      matchId: match.id ?? null,
    });

    this.emitProgress();
  }

  pause() {
    if (!this.player) return;
    this.player.pauseVideo();
  }

  play() {
    if (!this.player) return;
    this.awaitingAutoplay = false;
    this.clearAutoplayTimeout();
    this.player.playVideo();
  }

  stop() {
    if (!this.player) return;
    this.player.stopVideo();
    this.awaitingAutoplay = false;
    this.clearAutoplayTimeout();
    this.clearProgressInterval();
    this.emit('stateChange', {
      state: 'paused',
      rawState: this.playerStates.PAUSED,
      matchId: this.currentMatch?.id ?? null,
    });
  }

  mute() {
    if (!this.player || !this.player.mute) return;
    this.player.mute();
  }

  unmute() {
    if (!this.player || !this.player.unMute) return;
    this.player.unMute();
  }

  destroy() {
    this.clearAutoplayTimeout();
    this.clearProgressInterval();
    this.readyPromise = null;
    if (this.player) {
      this.player.destroy();
      this.player = null;
    }
    this.playerReady = false;
    this.namespace = null;
    this.currentMatch = null;
  }

  isPlaying(): boolean {
    if (!this.player) return false;
    return this.player.getPlayerState() === this.playerStates.PLAYING;
  }

  on<K extends EventKey>(event: K, listener: EventListener<K>) {
    const set = this.listeners.get(event) ?? new Set();
    set.add(listener as EventListener<EventKey>);
    this.listeners.set(event, set as Set<EventListener<any>>);
    return () => this.off(event, listener);
  }

  off<K extends EventKey>(event: K, listener: EventListener<K>) {
    const set = this.listeners.get(event);
    if (!set) return;
    set.delete(listener as EventListener<EventKey>);
    if (set.size === 0) {
      this.listeners.delete(event);
    }
  }

  once<K extends EventKey>(event: K, listener: EventListener<K>) {
    const unsubscribe = this.on(event, (payload: YoutubePlayerEventMap[K]) => {
      unsubscribe();
      listener(payload);
    });
    return unsubscribe;
  }

  private emit<K extends EventKey>(event: K, payload: YoutubePlayerEventMap[K]) {
    const set = this.listeners.get(event);
    if (!set) return;
    set.forEach((listener) => {
      try {
        listener(payload);
      } catch (error) {
        if (process.env.NODE_ENV !== 'production') {
          // eslint-disable-next-line no-console
          console.error('[YoutubePlayerManager] listener error', error);
        }
      }
    });
  }

  private async ensurePlayer() {
    if (this.player && this.playerReady) {
      return;
    }

    if (!this.namespace) {
      this.namespace = await loadYoutubeIframeAPI();
      if (this.namespace.PlayerState) {
        this.playerStates = {
          ...DEFAULT_PLAYER_STATES,
          ...this.namespace.PlayerState,
        };
      }
    }

    if (!this.container) {
      throw new Error('No container available for YouTube player.');
    }

    if (!this.player) {
      this.readyPromise = new Promise<void>((resolve) => {
        this.resolveReady = resolve;
      });

      const PlayerConstructor = this.namespace!.Player as unknown as new (
        element: HTMLElement,
        options: {
          width: string;
          height: string;
          videoId?: string;
          playerVars?: Record<string, string | number>;
          events?: {
            onReady?: () => void;
            onStateChange?: (event: PlayerEvent) => void;
            onError?: (event: PlayerErrorEvent) => void;
          };
        }
      ) => YoutubePlayerInstance;

      const events = {
        onReady: () => this.handleReady(),
        onStateChange: (event: PlayerEvent) => this.handleStateChange(event),
        onError: (event: PlayerErrorEvent) => this.handleError(event),
      };

      this.player = new PlayerConstructor(this.container, {
        width: '100%',
        height: '100%',
        playerVars: {
          playsinline: 1,
          rel: 0,
          modestbranding: 1,
          fs: 0,
          controls: 1,
        },
        events,
      });
    }

    if (!this.playerReady && this.readyPromise) {
      await this.readyPromise;
    }
  }

  private handleReady() {
    this.playerReady = true;
    this.resolveReady?.();
    this.resolveReady = null;
    this.emit('ready', undefined);
  }

  private handleStateChange(event: PlayerEvent) {
    const rawState = event.data;

    if (rawState === this.playerStates.PLAYING) {
      this.awaitingAutoplay = false;
      this.clearAutoplayTimeout();
      if (this.pendingStartSeconds !== null) {
        event.target.seekTo(this.pendingStartSeconds, true);
        this.pendingStartSeconds = null;
      }
      this.startProgressInterval();
      this.emit('stateChange', { state: 'playing', rawState, matchId: this.currentMatch?.id ?? null });
      return;
    }

    if (rawState === this.playerStates.PAUSED) {
      if (this.awaitingAutoplay) {
        this.emit('autoplayBlocked', { matchId: this.currentMatch?.id ?? null });
      }
      this.awaitingAutoplay = false;
      this.clearAutoplayTimeout();
      this.clearProgressInterval();
      this.emit('stateChange', { state: 'paused', rawState, matchId: this.currentMatch?.id ?? null });
      return;
    }

    if (rawState === this.playerStates.BUFFERING) {
      this.emit('stateChange', { state: 'buffering', rawState, matchId: this.currentMatch?.id ?? null });
      return;
    }

    if (rawState === this.playerStates.ENDED) {
      this.clearProgressInterval();
      this.emit('stateChange', { state: 'ended', rawState, matchId: this.currentMatch?.id ?? null });
      this.emit('ended', { reason: 'youtube_end', matchId: this.currentMatch?.id ?? null });
      return;
    }

    if (rawState === this.playerStates.CUED) {
      this.emit('stateChange', { state: 'cued', rawState, matchId: this.currentMatch?.id ?? null });
      return;
    }

    if (rawState === this.playerStates.UNSTARTED) {
      this.emit('stateChange', { state: 'unstarted', rawState, matchId: this.currentMatch?.id ?? null });
    }
  }

  private handleError(event: PlayerErrorEvent) {
    this.clearProgressInterval();
    this.clearAutoplayTimeout();
    const code = event.data;
    const message = this.mapErrorCode(code);
    this.emit('error', { code, message, matchId: this.currentMatch?.id ?? null });
  }

  private startProgressInterval() {
    this.clearProgressInterval();

    this.progressIntervalId = window.setInterval(() => {
      this.emitProgress();
      this.maybeStopAtEnd();
    }, PROGRESS_UPDATE_INTERVAL_MS);
  }

  private emitProgress() {
    if (!this.player) return;
    const currentSeconds = this.player.getCurrentTime();
    const durationSeconds = this.player.getDuration();
    this.emit('progress', {
      positionMs: Number.isFinite(currentSeconds) ? Math.max(0, currentSeconds * 1000) : 0,
      durationMs: Number.isFinite(durationSeconds) ? Math.max(0, durationSeconds * 1000) : null,
      matchId: this.currentMatch?.id ?? null,
    });
  }

  private maybeStopAtEnd() {
    if (!this.player || this.pendingEndSeconds === null) return;
    const currentSeconds = this.player.getCurrentTime();
    if (currentSeconds >= this.pendingEndSeconds) {
      this.player.pauseVideo();
      this.clearProgressInterval();
      this.emit('ended', { reason: 'end_ms', matchId: this.currentMatch?.id ?? null });
      this.pendingEndSeconds = null;
    }
  }

  private clearProgressInterval() {
    if (this.progressIntervalId !== null) {
      window.clearInterval(this.progressIntervalId);
      this.progressIntervalId = null;
    }
  }

  private clearAutoplayTimeout() {
    if (this.autoplayTimeoutId !== null) {
      window.clearTimeout(this.autoplayTimeoutId);
      this.autoplayTimeoutId = null;
    }
  }

  private mapErrorCode(code: number): string {
    switch (code) {
      case 2:
        return 'Invalid video parameter.';
      case 5:
        return 'HTML5 player error.';
      case 100:
        return 'Video not found.';
      case 101:
      case 150:
        return 'Video owner has disabled playback in embedded players.';
      default:
        return 'Unknown YouTube player error.';
    }
  }
}

export const youtubePlayerManager = YoutubePlayerManager.getInstance();
